import ConditionalWrapper from '@/components/ui/custom/ConditionalWrapper';
import { CoursePathGroupByEnum } from '@/components/ui/custom/dataDisplay/course/coursesList/CoursePathGroupBy';
import CourseCategoryFilter from '@/components/ui/custom/dataInput/CourseCategoryFilter';
import TextInput from '@/components/ui/custom/dataInput/TextInput';
import useAuth from '@/data/hook/useAuth';
import {
  CoursePathOrdering,
  ListCoursePathFilters,
} from '@/data/services/coursePathServices';
import { isSuperAdmin, isUnitAdmin } from '@/functions/auth';
import { Breakpoints } from '@/utils/Breakpoint';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import CourseFilters from './CourseFilters';
import CourseFilterSelect from './CourseFilterSelect';

type FiltersProps = {
  filters: ListCoursePathFilters;
  groupBy: CoursePathGroupByEnum;
  updateFilters(filter: ListCoursePathFilters): void;
  updateGroupBy(groupBy: CoursePathGroupByEnum): void;
};

export default function CoursePathFilters({
  filters,
  groupBy,
  updateFilters,
  updateGroupBy,
}: FiltersProps) {
  const { user } = useAuth();
  const [t] = useTranslation('translation', {
    keyPrefix: 'coursesView.admin.filters',
  });
  const isLg = useMediaQuery({
    minWidth: Breakpoints.lg,
  });
  const [search, setSearch] = useState(filters.search || '');

  function onChangeSearch(e: ChangeEvent<HTMLInputElement>) {
    const search = e.target.value;
    setSearch(search);
    updateFilters({ search });
  }
  function isGroupByNone(value: CoursePathGroupByEnum) {
    return value === CoursePathGroupByEnum.NONE;
  }
  function onDeselectGroupBy() {
    updateGroupBy(CoursePathGroupByEnum.NONE);
  }
  const groupByOptions = Object.values(CoursePathGroupByEnum).map(value => ({
    label: t(`groupByFilter.${value}`),
    value,
    hide: isGroupByNone(value),
  }));
  const orderings: (CoursePathOrdering | undefined)[] = [
    undefined,
    '-created_at',
    'created_at',
    '-last_update',
    '-class_count',
    'class_count',
    '-rating',
  ];
  function handleOrderByOptionPermissions(
    ordering: CoursePathOrdering | undefined,
  ) {
    if (ordering?.includes('class')) {
      return !isUnitAdmin(user?.userType);
    }
    if (ordering?.includes('rating')) {
      return !isSuperAdmin(user?.userType);
    }
    return ordering === undefined;
  }
  const orderByOptions = orderings.map(value => ({
    label: value ? t(`orderByFilter.${value}`) : t('orderByFilter.title'),
    value,
    hide: handleOrderByOptionPermissions(value),
  }));

  const onDeselect =
    filters.ordering !== undefined
      ? () => updateFilters({ ordering: undefined })
      : undefined;

  return (
    <div className="flex gap-2.5 lg:flex flex-wrap justify-between items-center lg:gap-y-4">
      <CourseCategoryFilter filter={filters} onFilter={updateFilters} />
      <ConditionalWrapper
        wrapper={<div className="flex items-center gap-2.5 grow xl:grow-0" />}
        condition={isLg}
      >
        <CourseFilters
          updateFilters={updateFilters}
          courses={filters.courseId}
        />

        <CourseFilterSelect
          className={{
            base: 'row-start-3 col-start-2 justify-self-end shrink-0',
          }}
          onDeselect={onDeselect}
          text={t('orderBy')}
          options={orderByOptions}
          selectedValue={filters.ordering}
          onSelect={ordering => updateFilters({ ordering })}
        />
        <CourseFilterSelect
          className={{ base: 'row-start-3 col-start-1 shrink-0' }}
          onDeselect={isGroupByNone(groupBy) ? undefined : onDeselectGroupBy}
          text={t('groupBy')}
          options={groupByOptions}
          selectedValue={groupBy}
          onSelect={updateGroupBy}
        />
        <TextInput
          className={{
            base: 'row-start-1 col-start-2 2xl:min-w-72 max-w-72 ml-auto xl:ml-0',
            input: 'w-full',
          }}
          placeholder={t('search')}
          value={search}
          onChange={onChangeSearch}
          icon={<MagnifyingGlassIcon className="w-4 h-4" />}
        />
      </ConditionalWrapper>
    </div>
  );
}
